/**
 * 实时多音引擎的主线程封装：AudioWorklet 采帧 + Worker 打分。
 *
 * 数据流：
 *   getUserMedia → AudioContext → MediaStreamSource → AudioWorkletNode
 *     → (每 128 帧 postMessage) 主线程累积缓冲 → 累积满一段窗口 → Worker
 *     → scoreExpected(期望音) → 结果回调 onResult(hit, notes)
 *
 * 判定策略（和弦「漏/错都判错」）：
 *   Worker 只做「对期望音打分」，主线程持有当前期望音集合（expectedNotes）。
 *   每个分析窗口结束：hit = confidence >= 命中阈值的期望音；主线程把 hit 与
 *   expectedNotes 比对，全部命中才回调「正确」，否则回调「错误/漏弹」。
 *
 * 与旧单音引擎（pitch.ts 自相关）并行：两者互不感知，PracticePanel 用开关
 * 决定走哪条路径。本类只管多音这条。
 */
import Worker from './practice-analyzer.worker?worker'
import { NoteOnsetEvent } from './NoteTracker'
// AudioWorklet 模块：静态 `?url` 导入（Vite 构建期把它作为独立资源输出并内联其
// URL）。两点关键：
//   1. 用**静态 import**，不用动态 `import('...?url')` —— 后者在 Vite 里不被可靠
//      解析，会拿到无效路径。
//   2. worklet 是 `.js` 而非 `.ts` —— `?url` 对 `.ts` 按静态资源处理、不保证走
//      TS→JS 转换，addModule 会加载到带类型标注的源码而语法报错。
import workletUrl from './practice-capture-worklet.js?url'

export type ExpectedNoteResult = {
	midi: number
	activation: number
	confidence: number
}

export type AnalysisResult = {
	/** 命中的期望音（midi，升序） */
	hit: number[]
	/** 每个期望音的置信度（供校准/调试） */
	notes: ExpectedNoteResult[]
	/** 本窗口新产生的击键事件（Note Tracking，区分同音重复/余音） */
	onsets: NoteOnsetEvent[]
	processingTimeMs: number
}

export type RealtimeEngineConfig = {
	/** 分析窗口时长（秒）。过短频谱分辨率不够（低音漏），过长判定滞后。 */
	windowSec?: number
	/** 命中阈值：confidence >= 该值的期望音判定为命中（第二阶段真实录音校准）。 */
	hitThreshold?: number
}

const DEFAULT_WINDOW_SEC = 0.12
const DEFAULT_HIT_THRESHOLD = 0.30

export class RealtimePolyphonicEngine {
	private audioCtx: AudioContext | null = null
	private stream: MediaStream | null = null
	private workletNode: AudioWorkletNode | null = null
	private worker: Worker | null = null
	private buffer: Float32Array
	private sampleRate = 48000
	private expectedNotes: number[] = []
	private cfg: Required<RealtimeEngineConfig>
	private analyzing = false
	private pendingResult: AnalysisResult | null = null
	/** 累计采样数（用于给每个分析窗口打时间戳，供 NoteTracker 判断状态时长） */
	private totalSamples = 0

	onResult: ((result: AnalysisResult) => void) | null = null

	constructor(cfg: RealtimeEngineConfig = {}) {
		this.cfg = {
			windowSec: cfg.windowSec ?? DEFAULT_WINDOW_SEC,
			hitThreshold: cfg.hitThreshold ?? DEFAULT_HIT_THRESHOLD,
		}
		this.buffer = new Float32Array(0)
	}

	/** 是否正在采音 */
	get listening() {
		return this.workletNode != null
	}

	/** 设置当前期望音集合（midi，升序去重）。换音/换和弦时调用。 */
	setExpected(notes: number[]) {
		this.expectedNotes = [ ...new Set(notes) ].sort((a, b) => a - b)
	}

	/**
	 * 开麦并启动采帧 + 分析循环。
	 * @returns 成功 / 失败（授权拒绝等）
	 */
	async start(): Promise<void> {
		if(this.workletNode) return
		const stream = await navigator.mediaDevices.getUserMedia({
			audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
		})
		const audioCtx = new AudioContext()
		// AudioWorklet 模块（顶部已静态 ?url 导入）
		await audioCtx.audioWorklet.addModule(workletUrl)
		const source = audioCtx.createMediaStreamSource(stream)
		const node = new AudioWorkletNode(audioCtx, 'practice-capture')
		source.connect(node)
		this.audioCtx = audioCtx
		this.stream = stream
		this.workletNode = node
		this.sampleRate = audioCtx.sampleRate
		this.worker = new Worker()

		node.port.onmessage = (evt: MessageEvent) => {
			const msg = evt.data
			if(!msg || msg.type !== 'pcm') return
			this.onPcm(msg.data as Float32Array)
		}
		this.worker.onmessage = (evt: MessageEvent) => {
			const msg = evt.data
			if(!msg || msg.type !== 'result') return
			this.analyzing = false
			const result: AnalysisResult = {
				hit: msg.hit as number[],
				notes: msg.notes as ExpectedNoteResult[],
				onsets: (msg.onsets as NoteOnsetEvent[]) ?? [],
				processingTimeMs: msg.processingTimeMs as number,
			}
			// 命中判定：期望音全命中才算正确（和弦漏/错都算错）
			this.onResult?.(result)
		}
	}

	/** 收到一帧 PCM，累积；满一个窗口就送 Worker 分析（非阻塞，一次一个）。 */
	private onPcm(frame: Float32Array) {
		const next = new Float32Array(this.buffer.length + frame.length)
		next.set(this.buffer)
		next.set(frame, this.buffer.length)
		this.buffer = next

		const needLen = Math.floor(this.cfg.windowSec * this.sampleRate)
		if(this.buffer.length < needLen) return
		// 取满一个窗口，丢掉已用的部分（保留余量，窗口重叠以跟踪快速换音）
		const window = this.buffer.subarray(0, needLen)
		const rest = this.buffer.subarray(needLen)
		this.buffer = new Float32Array(rest.length)
		this.buffer.set(rest)

		if(this.analyzing) return // 上一窗口还没回来，跳过（避免堆积）
		if(this.expectedNotes.length === 0) return
		this.analyzing = true
		// 打时间戳：本窗口起点 = 已消费采样数 / 采样率（跨窗口单调递增）
		const windowStartSec = this.totalSamples / this.sampleRate
		this.totalSamples += needLen
		this.worker?.postMessage({
			type: 'analyze',
			signal: window,
			sampleRate: this.sampleRate,
			expectedNotes: this.expectedNotes,
			hitThreshold: this.cfg.hitThreshold,
			windowStartSec,
		})
	}

	/** 停麦、释放资源。 */
	stop() {
		if(this.workletNode) {
			this.workletNode.port.onmessage = null
			this.workletNode.disconnect()
			this.workletNode = null
		}
		if(this.worker) {
			this.worker.onmessage = null
			this.worker.terminate()
			this.worker = null
		}
		if(this.stream) {
			this.stream.getTracks().forEach(t => t.stop())
			this.stream = null
		}
		if(this.audioCtx) {
			this.audioCtx.close()
			this.audioCtx = null
		}
		this.buffer = new Float32Array(0)
		this.analyzing = false
		this.totalSamples = 0
	}
}
